<div class="reports-page">
  <header class="page-header">
    <div>
      <h1>Scheduled Reports</h1>
      <p class="subtitle">
        Create recurring canned or custom flat-file reports, preview them before activation, and manage run history.
      </p>
    </div>

    <div class="header-actions" *ngIf="viewMode !== 'list'">
      <button mat-button type="button" *ngIf="viewMode === 'detail'" (click)="backToReportList()">Back to reports</button>
      <button mat-button type="button" *ngIf="viewMode === 'create' || viewMode === 'edit'" (click)="cancelEditor()">
        {{ detailReport && editingReportId ? 'Back to detail' : 'Back to list' }}
      </button>
    </div>
  </header>

  <section class="page-loading" *ngIf="isCompanyStateLoading || isBootstrapping">
    <mat-spinner [diameter]="32"></mat-spinner>
    <span>Loading scheduled reports</span>
  </section>

  <ng-container *ngIf="!isCompanyStateLoading && !isBootstrapping">
    <section *ngIf="viewMode === 'list'" class="list-shell">
      <div class="report-type-tabs">
        <div
          class="report-type-tab"
          *ngFor="let tab of reportTypeTabs"
          [class.active]="activeListTab === tab.value"
          [class.can-create]="currentPermissions.create"
          role="button"
          tabindex="0"
          (click)="setListTab(tab.value)"
          (keydown.enter)="setListTab(tab.value)"
          (keydown.space)="setListTab(tab.value); $event.preventDefault()"
        >
          <div class="report-type-copy">
            <span>{{ tab.label }}</span>
            <small>{{ tab.description }}</small>
          </div>

          <div class="report-type-actions" *ngIf="currentPermissions.create">
            <button
              mat-stroked-button
              type="button"
              (click)="startCreate(tab.value); $event.stopPropagation()"
            >
              Create
            </button>
          </div>
        </div>
      </div>

      <ng-container *ngIf="reports.length; else emptyState">
        <div class="toolbar">
          <mat-form-field appearance="outline">
            <mat-label>Search reports</mat-label>
            <input matInput [(ngModel)]="searchValue" placeholder="Name, scope, format, delivery" />
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Status</mat-label>
            <mat-select [(ngModel)]="statusFilter">
              <mat-option value="all">All statuses</mat-option>
              <mat-option value="draft">Draft</mat-option>
              <mat-option value="active">Active</mat-option>
              <mat-option value="paused">Paused</mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Scope</mat-label>
            <mat-select [(ngModel)]="scopeFilter">
              <mat-option value="all">All scopes</mat-option>
              <mat-option value="partner">Partner</mat-option>
              <mat-option value="company">Company</mat-option>
              <mat-option value="team">Team</mat-option>
              <mat-option value="global">Global</mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Delivery</mat-label>
            <mat-select [(ngModel)]="deliveryFilter">
              <mat-option value="all">All delivery types</mat-option>
              <mat-option value="ftp">SFTP</mat-option>
              <mat-option value="email">Email</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div class="table-shell" *ngIf="!isListLoading; else listLoading">
          <table
            class="reports-table desktop-table"
            *ngIf="filteredReports.length; else noFilteredReports"
            matSort
            [matSortActive]="reportListSort.active"
            [matSortDirection]="reportListSort.direction"
            [matSortDisableClear]="false"
            (matSortChange)="onReportListSortChange($event)"
          >
            <thead>
              <tr>
                <th mat-sort-header="name">Name</th>
                <th mat-sort-header="reportType">Tab type</th>
                <th mat-sort-header="scope">Scope</th>
                <th mat-sort-header="outputFormat">Format</th>
                <th mat-sort-header="deliveryChannel">Delivery</th>
                <th mat-sort-header="schedule">Schedule summary</th>
                <th mat-sort-header="status">Status</th>
                <th mat-sort-header="lastRunAt">Last run</th>
                <th mat-sort-header="lastResult">Last result</th>
                <th mat-sort-header="nextRunAt">Next run</th>
                <th class="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let report of filteredReports; trackBy: trackById">
                <td>
                  <div class="primary-cell">{{ report.name }}</div>
                  <div class="secondary-cell" *ngIf="report.description">{{ report.description }}</div>
                </td>
                <td>{{ getReportTypeLabel(report.reportType) }}</td>
                <td>{{ getScopeLabel(report) }}</td>
                <td>{{ getFormatLabel(report.outputFormat) }}</td>
                <td>{{ getDeliveryLabel(report.deliveryChannel) }}</td>
                <td>{{ getScheduleSummary(report) }}</td>
                <td>
                  <span class="status-pill" [class.active]="report.status === 'active'" [class.paused]="report.status === 'paused'">
                    {{ report.status }}
                  </span>
                </td>
                <td>{{ formatReportDateTime(report.lastRunAt, report.timezone) }}</td>
                <td>{{ getLastResultLabel(report) }}</td>
                <td>{{ formatReportDateTime(report.nextRunAt, report.timezone) }}</td>
                <td class="actions-col">
                  <button
                    mat-icon-button
                    type="button"
                    [matMenuTriggerFor]="reportActionsMenu"
                    [matMenuTriggerData]="{ report: report }"
                    aria-label="Report actions"
                  >
                    <mat-icon>more_vert</mat-icon>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="mobile-report-list" *ngIf="filteredReports.length">
            <article class="mobile-report-card" *ngFor="let report of filteredReports; trackBy: trackById">
              <div class="mobile-report-head">
                <div>
                  <h3>{{ report.name }}</h3>
                  <p>{{ getReportTypeLabel(report.reportType) }}</p>
                </div>
                <span class="status-pill" [class.active]="report.status === 'active'" [class.paused]="report.status === 'paused'">
                  {{ report.status }}
                </span>
              </div>

              <div class="mobile-report-grid">
                <div>
                  <span class="mobile-label">Scope</span>
                  <span>{{ getScopeLabel(report) }}</span>
                </div>
                <div>
                  <span class="mobile-label">Format</span>
                  <span>{{ getFormatLabel(report.outputFormat) }}</span>
                </div>
                <div>
                  <span class="mobile-label">Delivery</span>
                  <span>{{ getDeliveryLabel(report.deliveryChannel) }}</span>
                </div>
                <div>
                  <span class="mobile-label">Next run</span>
                  <span>{{ formatReportDateTime(report.nextRunAt, report.timezone) }}</span>
                </div>
              </div>

              <p class="mobile-schedule">{{ getScheduleSummary(report) }}</p>

              <div class="mobile-actions">
                <button
                  mat-stroked-button
                  type="button"
                  [matMenuTriggerFor]="reportActionsMenu"
                  [matMenuTriggerData]="{ report: report }"
                >
                  More
                </button>
              </div>
            </article>
          </div>
        </div>
      </ng-container>

      <ng-template #emptyState>
        <section class="empty-state">
          <mat-icon>description</mat-icon>
          <h2>No reports yet</h2>
        </section>
      </ng-template>

      <ng-template #listLoading>
        <section class="inline-loading">
          <mat-spinner [diameter]="28"></mat-spinner>
          <span>Loading reports</span>
        </section>
      </ng-template>

      <ng-template #noFilteredReports>
        <section class="empty-inline-state">
          <h3>No reports match the current filters.</h3>
          <p>Adjust the search text or filters to see more scheduled reports.</p>
        </section>
      </ng-template>

      <section
        class="group-product-report"
        *ngIf="canViewGroupProductOnboardingReport"
      >
        <mat-expansion-panel class="group-product-report__panel" [expanded]="false">
          <mat-expansion-panel-header>
            <mat-panel-title>
              <div class="group-product-report__title-row">
                <span>Product Approval Status</span>
                <span class="group-product-report__meta">{{ groupProductOnboardingReportVisibleRowCountLabel }}</span>
              </div>
            </mat-panel-title>
            <mat-panel-description>
              <div class="group-product-report__summary-pills">
                <button
                  type="button"
                  class="group-product-report__summary-pill all"
                  [class.active]="isGroupProductOnboardingStatusFilterSelected('all')"
                  (click)="setGroupProductOnboardingStatusFilter('all', $event)"
                >
                  All {{ groupProductOnboardingReportRows.length }}
                </button>
                <button
                  type="button"
                  class="group-product-report__summary-pill pending"
                  [class.active]="isGroupProductOnboardingStatusFilterSelected('pending')"
                  (click)="setGroupProductOnboardingStatusFilter('pending', $event)"
                >
                  Pending {{ groupProductOnboardingReportPendingCount }}
                </button>
                <button
                  type="button"
                  class="group-product-report__summary-pill ready"
                  [class.active]="isGroupProductOnboardingStatusFilterSelected('ready')"
                  (click)="setGroupProductOnboardingStatusFilter('ready', $event)"
                >
                  Ready {{ groupProductOnboardingReportReadyCount }}
                </button>
              </div>
            </mat-panel-description>
          </mat-expansion-panel-header>

          <section class="inline-loading group-product-report__loading" *ngIf="isGroupProductOnboardingReportLoading">
            <mat-spinner [diameter]="24"></mat-spinner>
            <span>Loading product approval status</span>
          </section>

          <ng-container *ngIf="!isGroupProductOnboardingReportLoading">
            <div class="group-product-report__body-header">
              <p class="group-product-report__body-copy">
                Internal overview of companies with approval-required payroll products and their readiness state.
              </p>
              <button
                mat-stroked-button
                type="button"
                [disabled]="!filteredGroupProductOnboardingReportRows.length"
                (click)="exportGroupProductOnboardingReport()"
              >
                Export CSV
              </button>
            </div>

            <div class="group-product-report__empty" *ngIf="!groupProductOnboardingReportRows.length">
              No companies with approval-required payroll products are showing status rows yet.
            </div>

            <div
              class="group-product-report__empty"
              *ngIf="groupProductOnboardingReportRows.length && !filteredGroupProductOnboardingReportRows.length"
            >
              No rows match the selected status filter.
            </div>

            <div
              class="table-shell group-product-report__table-shell"
              *ngIf="filteredGroupProductOnboardingReportRows.length"
            >
              <table class="reports-table reports-table--compact">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Provider</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>Requested By</th>
                    <th>Contact</th>
                    <th>Requested At</th>
                    <th>Ready By</th>
                    <th>Ready At</th>
                    <th class="actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of filteredGroupProductOnboardingReportRows; trackBy: trackGroupProductOnboardingRow">
                    <td>{{ row.companyName || '—' }}</td>
                    <td>{{ row.providerName || '—' }}</td>
                    <td>{{ row.planName || '—' }}</td>
                    <td>
                      <span
                        class="status-pill"
                        [class.active]="row.status === 'ready'"
                        [class.paused]="row.status === 'pending'"
                      >
                        {{ getGroupProductOnboardingStatusLabel(row) }}
                      </span>
                    </td>
                    <td>{{ row.userName || row.requestedBy.name || '—' }}</td>
                    <td>
                      <div>{{ row.userEmail || row.requestedBy.email || '—' }}</div>
                      <div class="secondary-cell" *ngIf="row.userPhone || row.requestedBy.phone">
                        {{ row.userPhone || row.requestedBy.phone }}
                      </div>
                    </td>
                    <td>{{ formatReportDateTime(row.requestedAt, 'America/Denver') }}</td>
                    <td>{{ row.readyBy.name || '—' }}</td>
                    <td>{{ row.readyAt ? formatReportDateTime(row.readyAt, 'America/Denver') : '—' }}</td>
                    <td class="actions-col group-product-report__actions-col">
                      <button
                        mat-stroked-button
                        type="button"
                        *ngIf="canMarkGroupProductOnboardingReady(row)"
                        [disabled]="isMarkingGroupProductOnboardingReady(row) || isDeletingGroupProductOnboardingRequest(row)"
                        (click)="markGroupProductOnboardingReadyFromReport(row)"
                      >
                        {{ isMarkingGroupProductOnboardingReady(row) ? 'Marking...' : 'Mark Ready' }}
                      </button>
                      <button
                        mat-button
                        color="warn"
                        type="button"
                        *ngIf="canDeleteGroupProductOnboardingRequest(row)"
                        [disabled]="isMarkingGroupProductOnboardingReady(row) || isDeletingGroupProductOnboardingRequest(row)"
                        (click)="deleteGroupProductOnboardingRequestFromReport(row)"
                      >
                        {{ isDeletingGroupProductOnboardingRequest(row) ? 'Deleting...' : 'Delete' }}
                      </button>
                      <span *ngIf="!canMarkGroupProductOnboardingReady(row) && !canDeleteGroupProductOnboardingRequest(row)">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </ng-container>
        </mat-expansion-panel>
      </section>
    </section>

    <section *ngIf="viewMode === 'create' || viewMode === 'edit'" class="editor-shell">
      <div class="editor-topline">
        <div>
          <h2>{{ viewMode === 'create' ? 'Create scheduled report' : 'Edit scheduled report' }}</h2>
          <p>
            Build the report basics, definition, delivery, schedule, and preview before activation.
          </p>
        </div>
        <div class="editor-type-summary">
          <span class="editor-type-summary-label">Report type</span>
          <strong>{{ getReportTypeLabel(reportForm.get('reportType')?.value) }}</strong>
        </div>
      </div>

      <form [formGroup]="reportForm" class="editor-grid">
        <article class="panel">
          <div class="panel-header">
            <span class="panel-step">1</span>
            <div>
              <h3>Report basics</h3>
              <p>Name the report, add a description, and set the scope.</p>
            </div>
          </div>

          <div class="form-grid two-up">
            <mat-form-field appearance="outline">
              <mat-label>Report name</mat-label>
              <input matInput formControlName="name" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Status</mat-label>
              <mat-select formControlName="status">
                <mat-option value="draft">Draft</mat-option>
                <mat-option value="active">Active</mat-option>
                <mat-option value="paused">Paused</mat-option>
              </mat-select>
            </mat-form-field>

            <div class="toggle-row">
              <mat-slide-toggle formControlName="showReportDates">Show report dates</mat-slide-toggle>
            </div>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Description</mat-label>
              <textarea matInput rows="3" formControlName="description"></textarea>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Scope type</mat-label>
              <mat-select formControlName="scopeType">
                <mat-option *ngFor="let scopeType of scopeTypes" [value]="scopeType.value">
                  {{ scopeType.label }}
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" *ngIf="reportForm.get('scopeType')?.value !== 'global'">
              <mat-label>{{ getScopeSelectorLabel(reportForm.get('scopeType')?.value || '') }}</mat-label>
              <mat-select multiple formControlName="scopeIds">
                <mat-option *ngIf="shouldShowSelectSearch(getScopeOptions(reportForm.get('scopeType')?.value || '').length)">
                  <ngx-mat-select-search [formControl]="scopeSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                </mat-option>
                <mat-option *ngFor="let scope of getFilteredScopeOptions(reportForm.get('scopeType')?.value || '')" [value]="scope.id">
                  {{ scope.label }}<span *ngIf="scope.secondaryLabel"> - {{ scope.secondaryLabel }}</span>
                </mat-option>
              </mat-select>
            </mat-form-field>

            <div class="info-card" *ngIf="reportForm.get('scopeType')?.value !== 'global' && !getScopeOptions(reportForm.get('scopeType')?.value || '').length">
              <strong *ngIf="reportForm.get('scopeType')?.value === 'team' && areTeamOptionsLoading; else noScopeOptionsTitle">
                Loading team options
              </strong>
              <ng-template #noScopeOptionsTitle>
                <strong>No {{ reportForm.get('scopeType')?.value }} options available</strong>
              </ng-template>
              <p *ngIf="reportForm.get('scopeType')?.value === 'team' && areTeamOptionsLoading">
                Team options are loading for the current scope.
              </p>
              <p *ngIf="reportForm.get('scopeType')?.value !== 'team' || !areTeamOptionsLoading">
                There are no selectable {{ reportForm.get('scopeType')?.value }} records for the current user.
              </p>
            </div>
          </div>
        </article>

        <article class="panel">
          <div class="panel-header">
            <span class="panel-step">2</span>
            <div>
              <h3>Data definition</h3>
              <p *ngIf="isTemplateReport; else customDefinitionIntro">
                Choose the canned report template and fill in the supported controls.
              </p>
              <ng-template #customDefinitionIntro>
                <p>Choose approved fields, filters, and sort rules for the flat-file report.</p>
              </ng-template>
            </div>
          </div>

          <ng-container *ngIf="isTemplateReport; else customDefinition">
            <div class="form-grid two-up">
              <mat-form-field appearance="outline">
                <mat-label>Report template</mat-label>
                <mat-select formControlName="templateKey">
                  <mat-option *ngIf="shouldShowSelectSearch(templates.length)">
                    <ngx-mat-select-search [formControl]="templateSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                  </mat-option>
                  <mat-option *ngFor="let template of getFilteredTemplates(); trackBy: trackById" [value]="template.key">
                    {{ template.label }}
                  </mat-option>
                </mat-select>
              </mat-form-field>

              <div class="info-card" *ngIf="selectedTemplate">
                <strong>{{ selectedTemplate.label }}</strong>
                <p>{{ selectedTemplate.description }}</p>
              </div>

              <ng-container [formGroup]="templateArgsGroup">
              <mat-form-field appearance="outline" *ngIf="selectedTemplateNeedsVendorSelection">
                <mat-label>Vendors</mat-label>
                <mat-select
                  multiple
                  formControlName="vendorSlugs"
                  (selectionChange)="onTemplateVendorSelectionChange()"
                >
                  <mat-option *ngIf="shouldShowSelectSearch(vendorOptions.length)">
                    <ngx-mat-select-search [formControl]="templateVendorSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                  </mat-option>
                  <mat-option [value]="'__all_vendors__'" (onSelectionChange)="onAllTemplateVendorsToggle($event)">All vendors</mat-option>
                  <mat-option *ngFor="let vendor of getFilteredVendorOptions(); trackBy: trackById" [value]="vendor.value">
                    {{ vendor.label }}
                  </mat-option>
                </mat-select>
                <mat-hint *ngIf="allTemplateVendorOptionsSelected">All vendors are included by default.</mat-hint>
              </mat-form-field>

              <mat-form-field appearance="outline" *ngIf="selectedTemplateNeedsProductSelection">
                <mat-label>Products</mat-label>
                <mat-select multiple formControlName="productSlugs">
                  <mat-option *ngIf="shouldShowSelectSearch(getAvailableTemplateProducts().length)">
                    <ngx-mat-select-search [formControl]="templateProductSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                  </mat-option>
                  <mat-option
                    *ngFor="let product of getFilteredTemplateProducts(); trackBy: trackById"
                    [value]="getTemplateProductOptionValue(product)"
                  >
                    {{ product.vendorName || product.vendorSlug }} / {{ product.productName || product.productSlug }}
                  </mat-option>
                </mat-select>
                <mat-hint>Leave blank to include all products from the selected vendors.</mat-hint>
              </mat-form-field>

              <div class="form-grid two-up full-width" *ngIf="selectedTemplateSupportsEnrollmentStatusFilter">
                <mat-form-field appearance="outline">
                  <mat-label>Enrollment status</mat-label>
                  <mat-select formControlName="enrollmentStatusFilter">
                    <mat-option *ngFor="let option of templateEnrollmentStatusFilters" [value]="option.value">
                      {{ option.label }}
                    </mat-option>
                  </mat-select>
                </mat-form-field>
                <div class="info-card">
                  <strong>Roster filter</strong>
                  <p>Choose whether to include all rows, only enrolled rows, or only rows currently marked `never_enrolled`.</p>
                </div>
              </div>

              <div class="toggle-row full-width">
                <mat-slide-toggle formControlName="includeUsersWithoutCurrentTeamMembership">
                  Include users without current team membership
                </mat-slide-toggle>
                <p>Include scoped users even if they do not currently have an active team membership row.</p>
              </div>
              </ng-container>

              <section class="builder-panel full-width" *ngIf="selectedTemplateSupportsColumnCustomization">
                <div class="builder-header">
                  <h4>Output columns</h4>
                  <p>Drag to reorder the export columns and edit the final header text inline.</p>
                </div>

                <div cdkDropList class="selected-list" (cdkDropListDropped)="onTemplateColumnDrop($event)">
                  <div
                    class="selected-card"
                    *ngFor="let column of templateColumnsArray.controls; let i = index"
                    [formGroup]="asFormGroup(column)"
                    cdkDrag
                  >
                    <div class="selected-card-head">
                      <span class="drag-handle" cdkDragHandle>drag_indicator</span>
                      <div class="selected-card-copy">
                        <strong>{{ getDisplayColumnHeader(column.get('header')?.value || column.get('defaultHeader')?.value || '') }}</strong>
                        <p>{{ getTemplateColumnFieldLabel(column.get('field')?.value || '') }}</p>
                      </div>
                    </div>

                    <div class="form-grid two-up compact">
                      <mat-form-field appearance="outline">
                        <mat-label>Column label</mat-label>
                        <input matInput formControlName="header" [placeholder]="column.get('defaultHeader')?.value || ''" />
                      </mat-form-field>

                      <div class="template-column-meta">
                        <span class="meta-label">Source field</span>
                        <strong>{{ column.get('field')?.value }}</strong>
                      </div>

                      <div class="arg-toggle">
                        <mat-checkbox formControlName="reportTotal" [disabled]="!isReportTotalField(column.get('field')?.value || '')">
                          Report total
                        </mat-checkbox>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <div class="notice-card warning" *ngIf="selectedTemplate?.key === 'partner_subscription_roster'">
                <h4>Subscription Status</h4>
                <p>Derived from product subscription history. This does not reflect user account status.</p>
              </div>

              <div class="notice-card" *ngIf="selectedTemplate?.key === 'survey_answers_by_user'">
                <h4>One row per survey submission</h4>
                <p>Answers uses survey `formData` only.</p>
              </div>

              <div class="notice-card" *ngIf="selectedTemplate?.key === 'survey_answers_by_question'">
                <h4>One row per extracted answer</h4>
                <p>Question-level rows are flattened from survey response JSON when possible. Preview fallback notes should be treated as informational.</p>
              </div>
            </div>
          </ng-container>

          <ng-template #customDefinition>
            <div class="dataset-grid">
              <button
                type="button"
                class="dataset-card"
                *ngFor="let dataset of datasetOptions; trackBy: trackById"
                [class.active]="reportForm.get('baseEntity')?.value === dataset.key"
                [class.disabled]="!dataset.enabled"
                [disabled]="!dataset.enabled"
                (click)="reportForm.get('baseEntity')?.setValue(dataset.key)"
              >
                <strong>{{ dataset.label }}</strong>
                <span>{{ dataset.note }}</span>
              </button>
            </div>

            <div class="toggle-row" [formGroup]="customAudienceGroup">
              <mat-slide-toggle formControlName="includeUsersWithoutCurrentTeamMembership">
                Include users without current team membership
              </mat-slide-toggle>
              <p>Include scoped users even if they do not currently have an active team membership row.</p>
            </div>

            <div class="subsection">
              <div class="builder-header">
                <h4>Output shape</h4>
                <p>Choose between row-level exports and grouped count reports.</p>
              </div>

              <mat-button-toggle-group formControlName="resultMode" aria-label="Custom report output shape">
                <mat-button-toggle *ngFor="let mode of customReportResultModes" [value]="mode.value">
                  {{ mode.label }}
                </mat-button-toggle>
              </mat-button-toggle-group>

              <div class="info-card">
                <strong>{{ isAggregatedCustomReport ? 'Grouped counts' : 'Detail rows' }}</strong>
                <p>{{ (isAggregatedCustomReport ? customReportResultModes[1] : customReportResultModes[0]).description }}</p>
              </div>
            </div>

            <div class="builder-grid">
              <section class="builder-panel">
                <div class="builder-header">
                  <h4>Field catalog</h4>
                  <p>Grouped by business area, not by raw SQL join name.</p>
                </div>

                <div class="info-card" *ngIf="!groupedFields.length">
                  <strong>No approved fields available yet</strong>
                  <p>The selected dataset is ready in the UI, but the backend field catalog has not advertised fields for it yet.</p>
                </div>

                <div class="field-group" *ngFor="let group of groupedFields; trackBy: trackByGroupName">
                  <h5>{{ group.group }}</h5>
                  <div class="field-row" *ngFor="let field of group.fields; trackBy: trackById">
                    <div class="field-copy">
                      <strong>{{ field.label }}</strong>
                      <p>{{ field.description }}</p>
                    </div>
                    <div class="field-meta">
                      <span class="type-badge">{{ field.type }}</span>
                      <button mat-stroked-button type="button" (click)="isAggregatedCustomReport ? addGroupBy(field) : addColumn(field)">
                        {{ isAggregatedCustomReport ? 'Add level' : 'Add column' }}
                      </button>
                    </div>
                  </div>
                </div>
              </section>

              <section class="builder-panel">
                <ng-container *ngIf="!isAggregatedCustomReport; else aggregatedDefinition">
                  <div class="builder-header">
                    <h4>Output columns</h4>
                    <p>Drag to reorder the final column sequence and choose field, template, or static-value output.</p>
                  </div>

                  <div class="empty-inline-state" *ngIf="!columnsArray.length">
                    Choose at least one output column.
                  </div>

                  <div cdkDropList class="selected-list" (cdkDropListDropped)="onColumnDrop($event)">
                    <div class="selected-card" *ngFor="let column of columnsArray.controls; let i = index" [formGroup]="asFormGroup(column)" cdkDrag>
                      <div class="selected-card-head">
                        <span class="drag-handle" cdkDragHandle>drag_indicator</span>
                        <div class="selected-card-copy">
                          <strong>{{ column.get('header')?.value || ('Column ' + (i + 1)) }}</strong>
                          <p>{{ column.get('kind')?.value | titlecase }} column</p>
                        </div>
                        <button mat-icon-button type="button" (click)="removeColumn(i)">
                          <mat-icon>close</mat-icon>
                        </button>
                      </div>

                      <div class="form-grid three-up compact">
                        <mat-form-field appearance="outline">
                          <mat-label>Column type</mat-label>
                          <mat-select formControlName="kind" (selectionChange)="onColumnKindChange(column)">
                            <mat-option *ngFor="let kind of reportColumnKinds" [value]="kind.value">
                              {{ kind.label }}
                            </mat-option>
                          </mat-select>
                        </mat-form-field>

                        <mat-form-field appearance="outline">
                          <mat-label>Column label</mat-label>
                          <input matInput formControlName="header" />
                        </mat-form-field>

                        <div class="arg-toggle">
                          <mat-checkbox formControlName="reportTotal" [disabled]="!canReportTotalColumn(column)">
                            Report total
                          </mat-checkbox>
                        </div>

                        <mat-form-field appearance="outline" *ngIf="column.get('kind')?.value === 'field'">
                          <mat-label>Transform</mat-label>
                          <mat-select formControlName="transform">
                            <mat-option *ngFor="let transform of outputTransforms" [value]="transform.value">
                              {{ transform.label }}
                            </mat-option>
                          </mat-select>
                        </mat-form-field>

                        <mat-form-field appearance="outline" *ngIf="column.get('kind')?.value === 'field'">
                          <mat-label>Source field</mat-label>
                          <mat-select formControlName="field" (selectionChange)="onColumnFieldChange(column)">
                            <mat-option *ngIf="shouldShowSelectSearch(getGroupedFieldCount())">
                              <ngx-mat-select-search [formControl]="fieldSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                            </mat-option>
                            <ng-container *ngFor="let group of getFilteredGroupedFields(); trackBy: trackByGroupName">
                              <mat-option *ngFor="let field of group.fields; trackBy: trackById" [value]="field.key">
                                {{ field.label }}
                              </mat-option>
                            </ng-container>
                          </mat-select>
                        </mat-form-field>

                        <mat-form-field appearance="outline" *ngIf="isDateFormatColumn(column)">
                          <mat-label>Date Format</mat-label>
                          <mat-select formControlName="dateFormatMode">
                            <mat-option *ngFor="let option of dateFormatOptions" [value]="option.value">
                              {{ option.label }}
                            </mat-option>
                          </mat-select>
                        </mat-form-field>

                        <mat-form-field appearance="outline" *ngIf="isCustomDateFormatColumn(column)">
                          <mat-label>Custom Date Format</mat-label>
                          <input matInput formControlName="customDateFormat" />
                        </mat-form-field>

                        <ng-container *ngIf="column.get('kind')?.value === 'field' && fieldRequiresArgs(column.get('field')?.value || '')">
                          <ng-container [formGroup]="getArgsGroup(column)">
                            <ng-container *ngFor="let arg of getFieldArgEntries(column.get('field')?.value || ''); trackBy: trackByArgKey">
                              <ng-container *ngIf="isVendorSelectionArg(arg.key, arg.schema); else standardColumnArg">
                                <mat-form-field appearance="outline">
                                  <mat-label>Vendors</mat-label>
                                  <mat-select multiple formControlName="vendorSlugs" (selectionChange)="onVendorSelectionChange(column)">
                                    <mat-option *ngIf="shouldShowSelectSearch(vendorOptions.length)">
                                      <ngx-mat-select-search [formControl]="semanticVendorSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                    </mat-option>
                                    <mat-option *ngFor="let vendor of getFilteredVendorOptions(semanticVendorSearchControl); trackBy: trackById" [value]="vendor.value">
                                      {{ vendor.label }}
                                    </mat-option>
                                  </mat-select>
                                </mat-form-field>

                                <mat-form-field appearance="outline">
                                  <mat-label>Products</mat-label>
                                  <mat-select multiple formControlName="productIds">
                                    <mat-option *ngIf="shouldShowSelectSearch(getAvailableVendorSelectionProducts(column).length)">
                                      <ngx-mat-select-search [formControl]="semanticProductSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                    </mat-option>
                                    <mat-option
                                      *ngFor="let product of getFilteredVendorSelectionProducts(column); trackBy: trackById"
                                      [value]="product.id"
                                    >
                                      {{ product.label }}
                                    </mat-option>
                                  </mat-select>
                                </mat-form-field>
                              </ng-container>

                              <ng-template #standardColumnArg>
                                <div class="arg-toggle" *ngIf="arg.schema.type === 'boolean'; else columnArgInput">
                                  <mat-slide-toggle [formControlName]="arg.key">{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-slide-toggle>
                                </div>
                                <ng-template #columnArgInput>
                                  <mat-form-field appearance="outline">
                                    <mat-label>{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-label>
                                    <input matInput [formControlName]="arg.key" />
                                  </mat-form-field>
                                </ng-template>
                              </ng-template>
                            </ng-container>
                          </ng-container>
                        </ng-container>

                        <ng-container *ngIf="column.get('kind')?.value === 'template'">
                          <mat-form-field appearance="outline" class="full-width">
                            <mat-label>Template</mat-label>
                            <textarea matInput rows="3" formControlName="template"></textarea>
                          </mat-form-field>

                          <div class="info-card full-width">
                            <strong>Runtime tokens are available inside templates</strong>
                            <p>
                              Use <code ngNonBindable>{{windowStart}}</code>, <code ngNonBindable>{{windowEnd}}</code>, or
                              <code ngNonBindable>{{asOfAt}}</code>, and add a format like
                              <code ngNonBindable>{{windowStart:YYYYMMDD}}</code> when needed.
                            </p>
                          </div>

                          <div class="full-width template-source-list">
                            <div class="template-source-card" *ngFor="let source of getColumnSourcesArray(column).controls; let sourceIndex = index" [formGroup]="asFormGroup(source)">
                              <div class="selected-card-head nested">
                                <strong>Source {{ sourceIndex + 1 }}</strong>
                                <button mat-icon-button type="button" (click)="removeTemplateSource(column, sourceIndex)">
                                  <mat-icon>close</mat-icon>
                                </button>
                              </div>

                              <div class="form-grid three-up compact">
                                <mat-form-field appearance="outline">
                                  <mat-label>Token</mat-label>
                                  <input matInput formControlName="token" />
                                </mat-form-field>

                                <mat-form-field appearance="outline">
                                  <mat-label>Field</mat-label>
                                  <mat-select formControlName="field" (selectionChange)="onTemplateSourceFieldChange(source)">
                                    <mat-option *ngIf="shouldShowSelectSearch(getGroupedFieldCount())">
                                      <ngx-mat-select-search [formControl]="fieldSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                    </mat-option>
                                    <ng-container *ngFor="let group of getFilteredGroupedFields(); trackBy: trackByGroupName">
                                      <mat-option *ngFor="let field of group.fields; trackBy: trackById" [value]="field.key">
                                        {{ field.label }}
                                      </mat-option>
                                    </ng-container>
                                  </mat-select>
                                </mat-form-field>

                                <mat-form-field appearance="outline">
                                  <mat-label>Transform</mat-label>
                                  <mat-select formControlName="transform">
                                    <mat-option *ngFor="let transform of outputTransforms" [value]="transform.value">
                                      {{ transform.label }}
                                    </mat-option>
                                  </mat-select>
                                </mat-form-field>

                                <ng-container *ngIf="fieldRequiresArgs(source.get('field')?.value || '')">
                                  <ng-container [formGroup]="getArgsGroup(source)">
                                    <ng-container *ngFor="let arg of getFieldArgEntries(source.get('field')?.value || ''); trackBy: trackByArgKey">
                                      <ng-container *ngIf="isVendorSelectionArg(arg.key, arg.schema); else standardSourceArg">
                                        <mat-form-field appearance="outline">
                                          <mat-label>Vendors</mat-label>
                                          <mat-select multiple formControlName="vendorSlugs" (selectionChange)="onVendorSelectionChange(source)">
                                            <mat-option *ngIf="shouldShowSelectSearch(vendorOptions.length)">
                                              <ngx-mat-select-search [formControl]="semanticVendorSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                            </mat-option>
                                            <mat-option *ngFor="let vendor of getFilteredVendorOptions(semanticVendorSearchControl); trackBy: trackById" [value]="vendor.value">
                                              {{ vendor.label }}
                                            </mat-option>
                                          </mat-select>
                                        </mat-form-field>

                                        <mat-form-field appearance="outline">
                                          <mat-label>Products</mat-label>
                                          <mat-select multiple formControlName="productIds">
                                            <mat-option *ngIf="shouldShowSelectSearch(getAvailableVendorSelectionProducts(source).length)">
                                              <ngx-mat-select-search [formControl]="semanticProductSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                            </mat-option>
                                            <mat-option
                                              *ngFor="let product of getFilteredVendorSelectionProducts(source); trackBy: trackById"
                                              [value]="product.id"
                                            >
                                              {{ product.label }}
                                            </mat-option>
                                          </mat-select>
                                        </mat-form-field>
                                      </ng-container>

                                      <ng-template #standardSourceArg>
                                        <div class="arg-toggle" *ngIf="arg.schema.type === 'boolean'; else sourceArgInput">
                                          <mat-slide-toggle [formControlName]="arg.key">{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-slide-toggle>
                                        </div>
                                        <ng-template #sourceArgInput>
                                          <mat-form-field appearance="outline">
                                            <mat-label>{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-label>
                                            <input matInput [formControlName]="arg.key" />
                                          </mat-form-field>
                                        </ng-template>
                                      </ng-template>
                                    </ng-container>
                                  </ng-container>
                                </ng-container>
                              </div>
                            </div>
                          </div>

                          <div class="builder-footer full-width">
                            <button mat-stroked-button type="button" (click)="addTemplateSource(column)">Add template source</button>
                          </div>
                        </ng-container>

                        <mat-form-field appearance="outline" class="full-width" *ngIf="column.get('kind')?.value === 'value'">
                          <mat-label>Static value</mat-label>
                          <input matInput formControlName="value" />
                        </mat-form-field>
                      </div>
                    </div>
                  </div>

                  <div class="builder-footer">
                    <button mat-stroked-button type="button" (click)="addColumn()">Add another column</button>
                  </div>
                </ng-container>

                <ng-template #aggregatedDefinition>
                  <div class="builder-header">
                    <h4>Group-by levels</h4>
                    <p>Each level becomes an output column. The final count column is added automatically.</p>
                  </div>

                  <div class="empty-inline-state" *ngIf="!groupByArray.length">
                    Choose at least one group-by level.
                  </div>

                  <div class="selected-list">
                    <div class="selected-card" *ngFor="let groupBy of groupByArray.controls; let i = index" [formGroup]="asFormGroup(groupBy)">
                      <div class="selected-card-head">
                        <div class="selected-card-copy">
                          <strong>{{ groupBy.get('header')?.value || ('Level ' + (i + 1)) }}</strong>
                          <p>{{ getFieldLabel(groupBy.get('field')?.value || '') }}</p>
                        </div>
                        <button mat-icon-button type="button" (click)="removeGroupBy(i)">
                          <mat-icon>close</mat-icon>
                        </button>
                      </div>

                      <div class="form-grid three-up compact">
                        <mat-form-field appearance="outline">
                          <mat-label>Group field</mat-label>
                          <mat-select formControlName="field" (selectionChange)="onGroupByFieldChange(groupBy)">
                            <mat-option *ngIf="shouldShowSelectSearch(getGroupedFieldCount())">
                              <ngx-mat-select-search [formControl]="fieldSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                            </mat-option>
                            <ng-container *ngFor="let group of getFilteredGroupedFields(); trackBy: trackByGroupName">
                              <mat-option *ngFor="let field of group.fields; trackBy: trackById" [value]="field.key">
                                {{ field.label }}
                              </mat-option>
                            </ng-container>
                          </mat-select>
                        </mat-form-field>

                        <mat-form-field appearance="outline">
                          <mat-label>Column label</mat-label>
                          <input matInput formControlName="header" />
                        </mat-form-field>

                        <mat-form-field appearance="outline" *ngIf="isDateFormatGroupBy(groupBy)">
                          <mat-label>Date Format</mat-label>
                          <mat-select formControlName="dateFormatMode">
                            <mat-option *ngFor="let option of dateFormatOptions" [value]="option.value">
                              {{ option.label }}
                            </mat-option>
                          </mat-select>
                        </mat-form-field>

                        <mat-form-field appearance="outline" *ngIf="isCustomDateFormatGroupBy(groupBy)">
                          <mat-label>Custom Date Format</mat-label>
                          <input matInput formControlName="customDateFormat" />
                        </mat-form-field>

                        <ng-container *ngIf="fieldRequiresArgs(groupBy.get('field')?.value || '')">
                          <ng-container [formGroup]="getArgsGroup(groupBy)">
                            <ng-container *ngFor="let arg of getFieldArgEntries(groupBy.get('field')?.value || ''); trackBy: trackByArgKey">
                              <ng-container *ngIf="isVendorSelectionArg(arg.key, arg.schema); else standardGroupByArg">
                                <mat-form-field appearance="outline">
                                  <mat-label>Vendors</mat-label>
                                  <mat-select multiple formControlName="vendorSlugs" (selectionChange)="onVendorSelectionChange(groupBy)">
                                    <mat-option *ngIf="shouldShowSelectSearch(vendorOptions.length)">
                                      <ngx-mat-select-search [formControl]="semanticVendorSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                    </mat-option>
                                    <mat-option *ngFor="let vendor of getFilteredVendorOptions(semanticVendorSearchControl); trackBy: trackById" [value]="vendor.value">
                                      {{ vendor.label }}
                                    </mat-option>
                                  </mat-select>
                                </mat-form-field>

                                <mat-form-field appearance="outline">
                                  <mat-label>Products</mat-label>
                                  <mat-select multiple formControlName="productIds">
                                    <mat-option *ngIf="shouldShowSelectSearch(getAvailableVendorSelectionProducts(groupBy).length)">
                                      <ngx-mat-select-search [formControl]="semanticProductSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                    </mat-option>
                                    <mat-option
                                      *ngFor="let product of getFilteredVendorSelectionProducts(groupBy); trackBy: trackById"
                                      [value]="product.id"
                                    >
                                      {{ product.label }}
                                    </mat-option>
                                  </mat-select>
                                </mat-form-field>
                              </ng-container>

                              <ng-template #standardGroupByArg>
                                <div class="arg-toggle" *ngIf="arg.schema.type === 'boolean'; else groupByArgInput">
                                  <mat-slide-toggle [formControlName]="arg.key">{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-slide-toggle>
                                </div>
                                <ng-template #groupByArgInput>
                                  <mat-form-field appearance="outline">
                                    <mat-label>{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-label>
                                    <input matInput [formControlName]="arg.key" />
                                  </mat-form-field>
                                </ng-template>
                              </ng-template>
                            </ng-container>
                          </ng-container>
                        </ng-container>
                      </div>
                    </div>
                  </div>

                  <div class="form-grid three-up compact" [formGroup]="aggregationGroup">
                    <mat-form-field appearance="outline">
                      <mat-label>Count distinct field</mat-label>
                      <mat-select formControlName="countDistinctField" (selectionChange)="onCountDistinctFieldChange()">
                        <mat-option *ngIf="shouldShowSelectSearch(getGroupedFieldCount())">
                          <ngx-mat-select-search [formControl]="fieldSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                        </mat-option>
                        <ng-container *ngFor="let group of getFilteredGroupedFields(); trackBy: trackByGroupName">
                          <mat-option *ngFor="let field of group.fields; trackBy: trackById" [value]="field.key">
                            {{ field.label }}
                          </mat-option>
                        </ng-container>
                      </mat-select>
                    </mat-form-field>

                    <ng-container *ngIf="fieldRequiresArgs(aggregationGroup.get('countDistinctField')?.value || '')">
                      <ng-container [formGroup]="aggregationCountDistinctArgsGroup">
                        <ng-container *ngFor="let arg of getFieldArgEntries(aggregationGroup.get('countDistinctField')?.value || ''); trackBy: trackByArgKey">
                          <div class="arg-toggle" *ngIf="arg.schema.type === 'boolean'; else countDistinctArgInput">
                            <mat-slide-toggle [formControlName]="arg.key">{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-slide-toggle>
                          </div>
                          <ng-template #countDistinctArgInput>
                            <mat-form-field appearance="outline">
                              <mat-label>{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-label>
                              <input matInput [formControlName]="arg.key" />
                            </mat-form-field>
                          </ng-template>
                        </ng-container>
                      </ng-container>
                    </ng-container>

                    <mat-form-field appearance="outline">
                      <mat-label>Count column label</mat-label>
                      <input matInput formControlName="countHeader" />
                    </mat-form-field>

                    <div class="arg-toggle">
                      <mat-checkbox formControlName="reportTotal">
                        Report total
                      </mat-checkbox>
                    </div>

                    <div class="info-card">
                      <strong>Applied after filters</strong>
                      <p>Filters run before aggregation. The count distinct field is counted once per grouped row.</p>
                    </div>
                  </div>

                  <div class="builder-footer">
                    <button mat-stroked-button type="button" (click)="addGroupBy()">Add group-by level</button>
                  </div>
                </ng-template>
              </section>
            </div>

            <div class="subsection">
              <div class="builder-header">
                <h4>Filters</h4>
                <p>Use typed inputs instead of raw operators or JSON.</p>
              </div>

              <div class="selected-card" *ngFor="let filter of filtersArray.controls; let i = index" [formGroup]="asFormGroup(filter)">
                <div class="selected-card-head">
                  <strong>Filter {{ i + 1 }}</strong>
                  <button mat-icon-button type="button" (click)="removeFilter(i)">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>

                <div class="form-grid four-up compact">
                  <mat-form-field appearance="outline">
                    <mat-label>Field</mat-label>
                    <mat-select formControlName="field" (selectionChange)="onFilterFieldChange(filter)">
                      <mat-option *ngIf="shouldShowSelectSearch(getGroupedFieldCount())">
                        <ngx-mat-select-search [formControl]="fieldSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                      </mat-option>
                      <ng-container *ngFor="let group of getFilteredGroupedFields(); trackBy: trackByGroupName">
                        <mat-option *ngFor="let field of group.fields; trackBy: trackById" [value]="field.key">
                          {{ field.label }}
                        </mat-option>
                      </ng-container>
                    </mat-select>
                  </mat-form-field>

                  <mat-form-field appearance="outline">
                    <mat-label>Operator</mat-label>
                    <mat-select formControlName="operator">
                      <mat-option *ngFor="let operator of getFieldOperators(filter.get('field')?.value || '')" [value]="operator">
                        {{ getFieldOperatorLabel(operator) }}
                      </mat-option>
                    </mat-select>
                  </mat-form-field>

                  <div class="toggle-row compact">
                    <mat-slide-toggle [checked]="isRuntimeFilter(filter)" (change)="setRuntimeFilterMode(filter, $event.checked)">
                      Ask at run time
                    </mat-slide-toggle>
                  </div>

                  <mat-form-field appearance="outline" *ngIf="!isRuntimeFilter(filter) && filterUsesSingleSelect(filter)">
                    <mat-label>Value</mat-label>
                    <mat-select formControlName="valueText">
                      <mat-option *ngFor="let option of getFilterValueOptions(filter); trackBy: trackById" [value]="option.value">
                        {{ option.label }}
                      </mat-option>
                    </mat-select>
                  </mat-form-field>

                  <mat-form-field appearance="outline" *ngIf="!isRuntimeFilter(filter) && filterUsesMultiSelect(filter)">
                    <mat-label>Values</mat-label>
                    <mat-select multiple formControlName="optionValues">
                      <mat-option *ngFor="let option of getFilterValueOptions(filter); trackBy: trackById" [value]="option.value">
                        {{ option.label }}
                      </mat-option>
                    </mat-select>
                  </mat-form-field>

                  <mat-form-field appearance="outline" *ngIf="!isRuntimeFilter(filter) && !filterUsesFiniteOptions(filter) && filterOperatorUsesValue(filter.get('operator')?.value) && (getFieldType(filter.get('field')?.value || '') === 'string' || getFieldType(filter.get('field')?.value || '') === 'enum')">
                    <mat-label>{{ filterOperatorUsesMultipleValues(filter.get('operator')?.value) ? 'Values' : 'Value' }}</mat-label>
                    <input matInput *ngIf="!filterOperatorUsesMultipleValues(filter.get('operator')?.value)" formControlName="valueText" />
                    <textarea matInput rows="2" *ngIf="filterOperatorUsesMultipleValues(filter.get('operator')?.value)" formControlName="multiValueText"></textarea>
                  </mat-form-field>

                  <mat-form-field appearance="outline" *ngIf="!isRuntimeFilter(filter) && filterOperatorUsesValue(filter.get('operator')?.value) && getFieldType(filter.get('field')?.value || '') === 'number'">
                    <mat-label>Value</mat-label>
                    <input matInput type="number" formControlName="valueNumber" />
                  </mat-form-field>

                  <mat-form-field appearance="outline" *ngIf="!isRuntimeFilter(filter) && filterOperatorUsesValue(filter.get('operator')?.value) && getFieldType(filter.get('field')?.value || '') === 'boolean'">
                    <mat-label>Value</mat-label>
                    <mat-select formControlName="valueBoolean">
                      <mat-option value="true">True</mat-option>
                      <mat-option value="false">False</mat-option>
                    </mat-select>
                  </mat-form-field>

                  <mat-form-field appearance="outline" *ngIf="!isRuntimeFilter(filter) && filterOperatorUsesValue(filter.get('operator')?.value) && getFieldType(filter.get('field')?.value || '') === 'date'">
                    <mat-label>Value</mat-label>
                    <input matInput type="date" formControlName="valueDate" />
                  </mat-form-field>

                  <mat-form-field appearance="outline" *ngIf="!isRuntimeFilter(filter) && filterOperatorUsesValue(filter.get('operator')?.value) && getFieldType(filter.get('field')?.value || '') === 'datetime'">
                    <mat-label>Value</mat-label>
                    <input matInput type="datetime-local" formControlName="valueDate" />
                  </mat-form-field>

                  <ng-container *ngIf="fieldRequiresArgs(filter.get('field')?.value || '')">
                    <ng-container [formGroup]="getArgsGroup(filter)">
                      <ng-container *ngFor="let arg of getFieldArgEntries(filter.get('field')?.value || ''); trackBy: trackByArgKey">
                        <ng-container *ngIf="isVendorSelectionArg(arg.key, arg.schema); else standardFilterArg">
                          <mat-form-field appearance="outline">
                            <mat-label>Vendors</mat-label>
                            <mat-select multiple formControlName="vendorSlugs" (selectionChange)="onVendorSelectionChange(filter)">
                              <mat-option *ngIf="shouldShowSelectSearch(vendorOptions.length)">
                                <ngx-mat-select-search [formControl]="semanticVendorSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                              </mat-option>
                              <mat-option *ngFor="let vendor of getFilteredVendorOptions(semanticVendorSearchControl); trackBy: trackById" [value]="vendor.value">
                                {{ vendor.label }}
                              </mat-option>
                            </mat-select>
                          </mat-form-field>

                          <mat-form-field appearance="outline">
                            <mat-label>Products</mat-label>
                            <mat-select multiple formControlName="productIds">
                              <mat-option *ngIf="shouldShowSelectSearch(getAvailableVendorSelectionProducts(filter).length)">
                                <ngx-mat-select-search [formControl]="semanticProductSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                              </mat-option>
                              <mat-option
                                *ngFor="let product of getFilteredVendorSelectionProducts(filter); trackBy: trackById"
                                [value]="product.id"
                              >
                                {{ product.label }}
                              </mat-option>
                            </mat-select>
                          </mat-form-field>
                        </ng-container>

                        <ng-template #standardFilterArg>
                          <div class="arg-toggle" *ngIf="arg.schema.type === 'boolean'; else filterArgInput">
                            <mat-slide-toggle [formControlName]="arg.key">{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-slide-toggle>
                          </div>
                          <ng-template #filterArgInput>
                            <mat-form-field appearance="outline">
                              <mat-label>{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-label>
                              <input matInput [formControlName]="arg.key" />
                            </mat-form-field>
                          </ng-template>
                        </ng-template>
                      </ng-container>
                    </ng-container>
                  </ng-container>
                </div>
              </div>

              <button mat-stroked-button type="button" (click)="addFilter()">Add filter</button>
            </div>

          </ng-template>

          <div class="subsection" *ngIf="!isTemplateReport || selectedTemplateSupportsSortCustomization">
            <div class="builder-header">
              <h4>Sort</h4>
              <p>Sort controls change the order of output rows in the final file.</p>
            </div>

            <div class="info-card">
              <strong>The first sort row has the highest priority.</strong>
              <p>Only sortable fields are available here. Drag rows to change the top-to-bottom precedence.</p>
            </div>

            <div class="empty-inline-state" *ngIf="!sortArray.length">
              Add a sort row when the export needs a predictable reading order.
            </div>

            <div cdkDropList class="selected-list" (cdkDropListDropped)="onSortDrop($event)">
              <div class="selected-card" *ngFor="let sort of sortArray.controls; let i = index" [formGroup]="asFormGroup(sort)" cdkDrag>
                <div class="selected-card-head">
                  <span class="drag-handle" cdkDragHandle>drag_indicator</span>
                  <div class="selected-card-copy">
                    <strong>{{ getFieldLabel(sort.get('field')?.value || '') }}</strong>
                    <p>Priority {{ i + 1 }} - {{ getSortDirectionLabel(sort.get('direction')?.value) }}</p>
                  </div>
                  <button mat-icon-button type="button" (click)="removeSort(i)">
                    <mat-icon>delete</mat-icon>
                  </button>
                </div>

                <div class="form-grid two-up compact">
                  <mat-form-field appearance="outline">
                    <mat-label>Field</mat-label>
                    <mat-select formControlName="field" (selectionChange)="onSortFieldChange(sort)">
                      <mat-option *ngIf="shouldShowSelectSearch(getSortableFields().length)">
                        <ngx-mat-select-search [formControl]="sortFieldSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                      </mat-option>
                      <mat-option *ngFor="let field of getFilteredSortableFields(); trackBy: trackById" [value]="field.key">
                        {{ field.label }}
                      </mat-option>
                    </mat-select>
                  </mat-form-field>

                  <mat-form-field appearance="outline">
                    <mat-label>Direction</mat-label>
                    <mat-select formControlName="direction">
                      <mat-option value="asc">Ascending</mat-option>
                      <mat-option value="desc">Descending</mat-option>
                    </mat-select>
                  </mat-form-field>

                  <ng-container *ngIf="fieldRequiresArgs(sort.get('field')?.value || '')">
                    <ng-container [formGroup]="getArgsGroup(sort)">
                      <ng-container *ngFor="let arg of getFieldArgEntries(sort.get('field')?.value || ''); trackBy: trackByArgKey">
                        <ng-container *ngIf="isVendorSelectionArg(arg.key, arg.schema); else standardSortArg">
                          <mat-form-field appearance="outline">
                            <mat-label>Vendors</mat-label>
                            <mat-select multiple formControlName="vendorSlugs" (selectionChange)="onVendorSelectionChange(sort)">
                              <mat-option *ngIf="shouldShowSelectSearch(vendorOptions.length)">
                                <ngx-mat-select-search [formControl]="semanticVendorSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                              </mat-option>
                              <mat-option *ngFor="let vendor of getFilteredVendorOptions(semanticVendorSearchControl); trackBy: trackById" [value]="vendor.value">
                                {{ vendor.label }}
                              </mat-option>
                            </mat-select>
                          </mat-form-field>

                          <mat-form-field appearance="outline">
                            <mat-label>Products</mat-label>
                            <mat-select multiple formControlName="productIds">
                              <mat-option *ngIf="shouldShowSelectSearch(getAvailableVendorSelectionProducts(sort).length)">
                                <ngx-mat-select-search [formControl]="semanticProductSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                              </mat-option>
                              <mat-option
                                *ngFor="let product of getFilteredVendorSelectionProducts(sort); trackBy: trackById"
                                [value]="product.id"
                              >
                                {{ product.label }}
                              </mat-option>
                            </mat-select>
                          </mat-form-field>
                        </ng-container>

                        <ng-template #standardSortArg>
                          <div class="arg-toggle" *ngIf="arg.schema.type === 'boolean'; else sortArgInput">
                            <mat-slide-toggle [formControlName]="arg.key">{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-slide-toggle>
                          </div>
                          <ng-template #sortArgInput>
                            <mat-form-field appearance="outline">
                              <mat-label>{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-label>
                              <input matInput [formControlName]="arg.key" />
                            </mat-form-field>
                          </ng-template>
                        </ng-template>
                      </ng-container>
                    </ng-container>
                  </ng-container>
                </div>
              </div>
            </div>

            <div class="builder-footer">
              <button mat-stroked-button type="button" (click)="addSort()">Add sort</button>
            </div>
          </div>
        </article>

        <article class="panel">
          <div class="panel-header">
            <span class="panel-step">3</span>
            <div>
              <h3>Output</h3>
              <p>Select the file format and naming pattern.</p>
            </div>
          </div>

          <div class="form-grid two-up">
            <mat-form-field appearance="outline">
              <mat-label>Output format</mat-label>
              <mat-select formControlName="outputFormat">
                <mat-option *ngFor="let format of outputFormats" [value]="format.value">{{ format.label }}</mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>File name pattern</mat-label>
              <input matInput formControlName="fileNamePattern" />
            </mat-form-field>
          </div>

          <div class="info-card">
            <strong>File name preview</strong>
            <p>{{ fileNamePreview }}</p>
          </div>
        </article>

        <article class="panel">
          <div class="panel-header">
            <span class="panel-step">4</span>
            <div>
              <h3>Delivery</h3>
              <p>Select exactly one delivery channel.</p>
            </div>
          </div>

          <div class="form-grid two-up">
            <mat-form-field appearance="outline">
              <mat-label>Delivery channel</mat-label>
              <mat-select formControlName="deliveryChannel">
                <mat-option *ngFor="let channel of deliveryChannels" [value]="channel.value">{{ channel.label }}</mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <div class="form-grid two-up" [formGroup]="deliveryConfigGroup" *ngIf="reportForm.get('deliveryChannel')?.value === 'ftp'">
            <mat-form-field appearance="outline">
              <mat-label>Host</mat-label>
              <input matInput formControlName="host" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Port</mat-label>
              <input matInput type="number" formControlName="port" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Username</mat-label>
              <input matInput formControlName="username" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Folder</mat-label>
              <input matInput formControlName="folder" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Password secret reference</mat-label>
              <mat-select formControlName="passwordSecretKey">
                <mat-option value="">None</mat-option>
                <mat-option *ngFor="let secret of sftpPasswordSecretReferences" [value]="secret.reference">
                  {{ secret.name }}
                </mat-option>
              </mat-select>
              <mat-hint>{{ sftpSecretPrefixes.password }}</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Private key secret reference</mat-label>
              <mat-select formControlName="privateKeySecretKey">
                <mat-option value="">None</mat-option>
                <mat-option *ngFor="let secret of sftpPrivateKeySecretReferences" [value]="secret.reference">
                  {{ secret.name }}
                </mat-option>
              </mat-select>
              <mat-hint>{{ sftpSecretPrefixes.private_key }}</mat-hint>
            </mat-form-field>

            <button mat-stroked-button type="button" (click)="addSftpSecret('password', 'passwordSecretKey')">
              Add password secret
            </button>

            <button mat-stroked-button type="button" (click)="addSftpSecret('private_key', 'privateKeySecretKey')">
              Add private key secret
            </button>

            <mat-form-field appearance="outline">
              <mat-label>File name prefix</mat-label>
              <input matInput formControlName="fileNamePrefix" />
            </mat-form-field>

            <div class="toggle-row compact">
              <mat-slide-toggle formControlName="pgpEnabled">PGP enabled</mat-slide-toggle>
              <p>Never display stored secret values. Use configured references instead.</p>
            </div>

            <mat-form-field appearance="outline" *ngIf="reportForm.get('deliveryConfig.pgpEnabled')?.value">
              <mat-label>PGP public key secret reference</mat-label>
              <mat-select formControlName="publicKeySecretKey">
                <mat-option value="">None</mat-option>
                <mat-option *ngFor="let secret of sftpPgpPublicKeySecretReferences" [value]="secret.reference">
                  {{ secret.name }}
                </mat-option>
              </mat-select>
              <mat-hint>{{ sftpSecretPrefixes.pgp_public_key }}</mat-hint>
            </mat-form-field>

            <button
              mat-stroked-button
              type="button"
              *ngIf="reportForm.get('deliveryConfig.pgpEnabled')?.value"
              (click)="addSftpSecret('pgp_public_key', 'publicKeySecretKey')"
            >
              Add PGP public key secret
            </button>
          </div>

          <div class="form-grid two-up" [formGroup]="deliveryConfigGroup" *ngIf="reportForm.get('deliveryChannel')?.value !== 'ftp'">
            <mat-form-field appearance="outline">
              <mat-label>To</mat-label>
              <textarea matInput rows="2" formControlName="toText" placeholder="recipient@example.com, second@example.com"></textarea>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>CC</mat-label>
              <input matInput formControlName="ccText" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>BCC</mat-label>
              <input matInput formControlName="bccText" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Subject template</mat-label>
              <input matInput formControlName="subjectTemplate" />
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Body template</mat-label>
              <textarea matInput rows="4" formControlName="bodyTemplate"></textarea>
            </mat-form-field>
          </div>
        </article>

        <article class="panel">
          <div class="panel-header">
            <span class="panel-step">5</span>
            <div>
              <h3>Schedule</h3>
              <p *ngIf="!isTeamMembersRuntimeSnapshot">Choose the time zone, cadence, and reporting window.</p>
              <p *ngIf="isTeamMembersRuntimeSnapshot">Choose the time zone and cadence for the membership snapshot.</p>
            </div>
          </div>

          <div class="form-grid two-up">
            <mat-form-field appearance="outline">
              <mat-label>Time zone</mat-label>
              <input matInput formControlName="timezone" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Frequency</mat-label>
              <mat-select formControlName="scheduleType">
                <mat-option *ngFor="let type of scheduleTypes" [value]="type.value">{{ type.label }}</mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <div class="form-grid two-up" [formGroup]="scheduleConfigGroup">
            <mat-form-field appearance="outline" *ngIf="isWeeklySchedule">
              <mat-label>Days of week</mat-label>
              <mat-select multiple formControlName="daysOfWeek">
                <mat-option *ngFor="let day of dayOptions" [value]="day.value">{{ day.label }}</mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" *ngIf="isMonthlySchedule">
              <mat-label>Day of month</mat-label>
              <input matInput type="number" formControlName="dayOfMonth" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Time of day</mat-label>
              <input matInput type="time" formControlName="time" />
            </mat-form-field>

            <mat-form-field appearance="outline" *ngIf="!isTeamMembersRuntimeSnapshot">
              <mat-label>Window mode</mat-label>
              <mat-select formControlName="windowMode">
                <mat-option *ngFor="let mode of windowModes" [value]="mode.value">{{ mode.label }}</mat-option>
              </mat-select>
            </mat-form-field>

            <div class="info-card" *ngIf="isTeamMembersRuntimeSnapshot">
              <strong>Window mode does not apply</strong>
              <p>Team Members includes memberships active when the report runs. Change as-of mode to Window end to build the membership snapshot at the end of a reporting window.</p>
            </div>

            <mat-form-field appearance="outline" *ngIf="isRollingWindow">
              <mat-label>Number of trailing days</mat-label>
              <input matInput type="number" formControlName="rollingDays" />
            </mat-form-field>
          </div>
        </article>

        <article class="panel">
          <div class="panel-header">
            <span class="panel-step">6</span>
            <div>
              <h3>As-of behavior</h3>
              <p>As-of date controls which historical state is considered true when building the report.</p>
            </div>
          </div>

          <div class="form-grid one-up" [formGroup]="temporalGroup">
            <mat-form-field appearance="outline" *ngIf="!isSubscriptionCoverageDataset()">
              <mat-label>As-of mode</mat-label>
              <mat-select formControlName="asOfMode" [disabled]="isTemplateReport">
                <mat-option *ngFor="let mode of asOfModes" [value]="mode.value">{{ mode.label }}</mat-option>
              </mat-select>
            </mat-form-field>

            <div class="info-card" *ngIf="isTemplateReport">
              <strong>Canned templates use run-time as-of mode today</strong>
              <p>The current backend template implementation fixes scheduled runs to run time. Manual reruns can still override the as-of date.</p>
            </div>

            <div class="info-card" *ngIf="isProductEnrollmentEventsDataset()">
              <strong>Product enrollment events are raw history rows</strong>
              <p>Window and as-of settings do not filter these rows by event date. Add Created At or Modified At filters when the report should include only events from a date range.</p>
            </div>

            <div class="mode-help" *ngIf="!isTemplateReport && !isSubscriptionCoverageDataset()">
              <div *ngFor="let mode of asOfModes">
                <strong>{{ mode.label }}</strong>
                <p>{{ mode.help }}</p>
              </div>
            </div>
          </div>
        </article>

        <article class="panel preview-panel">
          <div class="panel-header">
            <span class="panel-step">7</span>
            <div>
              <h3>Preview</h3>
              <p>Preview should succeed before the report is activated.</p>
            </div>
          </div>

          <div class="notice-card">
            <mat-slide-toggle
              [checked]="editorIncludeDemoUsers"
              [disabled]="isPreviewLoading"
              (change)="changeEditorDemoPreview($event.checked)">
              Include demo users
            </mat-slide-toggle>
            <p>Applies only to this preview. Other report filters still apply.</p>
          </div>

          <div class="inline-loading" *ngIf="isPreviewLoading">
            <mat-spinner [diameter]="28"></mat-spinner>
            <span>Building preview</span>
          </div>

          <div class="notice-card error" *ngIf="previewError">
            <h4>Preview failed</h4>
            <p>{{ previewError }}</p>
          </div>

          <div class="notice-card sensitive-preview-notice" *ngIf="preview && editorSensitivePreviewMode">
            <div>
              <h4>{{ editorSensitivePreviewMode === 'revealed' ? 'Full SSNs are visible' : 'SSNs are masked' }}</h4>
              <p *ngIf="editorSensitivePreviewMode === 'masked'">Every full SSN is masked without being decrypted.</p>
              <p *ngIf="editorSensitivePreviewMode === 'masked' && !sensitivePreviewBusinessPurposes.length">Full SSN reveal is unavailable because no approved business purposes are configured.</p>
              <p *ngIf="editorSensitivePreviewMode === 'revealed'">Full SSNs are displayed only for the approved purpose recorded with this audited preview.</p>
            </div>
            <button
              mat-stroked-button
              color="warn"
              type="button"
              *ngIf="editorSensitivePreviewMode === 'masked'"
              (click)="revealEditorFullSsns()"
              [disabled]="isPreviewLoading || !sensitivePreviewBusinessPurposes.length"
            >
              Reveal full SSNs
            </button>
            <button
              mat-stroked-button
              type="button"
              *ngIf="editorSensitivePreviewMode === 'revealed'"
              (click)="maskEditorSsns()"
            >
              Mask SSNs
            </button>
          </div>

          <ng-container *ngIf="preview">
            <div class="preview-meta">
              <div>
                <span class="meta-label">Effective scope</span>
                <span>{{ reportForm.get('scopeType')?.value | titlecase }} - {{ getSelectedScopeLabel(reportForm.get('scopeType')?.value || '') }}</span>
              </div>
              <div>
                <span class="meta-label">Row count</span>
                <span>{{ preview.totalCount }}</span>
              </div>
              <div>
                <span class="meta-label">File name</span>
                <span>{{ fileNamePreview }}</span>
              </div>
              <div>
                <span class="meta-label">As-of date</span>
                <span>{{ formatReportDateTime(preview.runContext.asOfAt, preview.runContext.timezone) }}</span>
              </div>
              <div>
                <span class="meta-label">Applied data window start</span>
                <span>{{ formatReportDateTime(preview.runContext.windowStart, preview.runContext.timezone) }}</span>
              </div>
              <div>
                <span class="meta-label">Applied data window end</span>
                <span>{{ formatReportDateTime(preview.runContext.windowEnd, preview.runContext.timezone) }}</span>
              </div>
            </div>

            <div class="warning-list" *ngIf="preview.warnings.length">
              <h4>Warnings</h4>
              <ul>
                <li *ngFor="let warning of preview.warnings">{{ warning }}</li>
              </ul>
            </div>

            <div class="empty-inline-state" *ngIf="!preview.sampleRows.length">
              <h4>No preview rows</h4>
              <p>Adjust the applied data window, scope, filters, or as-of behavior to find matching rows.</p>
            </div>

            <div class="table-shell" [class.preview-scroll-shell]="shouldUseScrollablePreview(preview)" *ngIf="preview.sampleRows.length">
              <table class="reports-table preview-table">
                <thead>
                  <tr>
                    <th *ngFor="let header of getColumnPreviewHeaders(preview)">
                      <button type="button" class="preview-sort-button" (click)="togglePreviewSort('editor', header)">
                        <span>{{ getDisplayColumnHeader(header) }}</span>
                        <span class="preview-sort-indicator" *ngIf="getPreviewSortDirection('editor', header) === 'asc'">arrow_upward</span>
                        <span class="preview-sort-indicator" *ngIf="getPreviewSortDirection('editor', header) === 'desc'">arrow_downward</span>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of getSortedPreviewRows(preview, 'editor')">
                    <td
                      *ngFor="let header of getColumnPreviewHeaders(preview)"
                      [class.preview-cell-multiline]="isPreviewCellMultiline(header, getPreviewCellValue(preview, row, header))"
                    >
                      {{ getPreviewCellText(header, getPreviewCellValue(preview, row, header)) }}
                    </td>
                  </tr>
                </tbody>
                <tfoot *ngIf="shouldShowPreviewTotals(preview, 'editor')">
                  <tr class="preview-total-row">
                    <td
                      *ngFor="let header of getColumnPreviewHeaders(preview)"
                      [class.preview-total-label]="isPreviewTotalLabelColumn(preview, 'editor', header)"
                      [class.preview-total-value]="isPreviewTotalValueColumn(preview, 'editor', header)"
                    >
                      {{ getPreviewTotalCellText(preview, 'editor', header) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div class="notice-card warning" *ngIf="isPreviewTotalFallbackSampleOnly(preview, 'editor')">
              <h4>Preview totals use sample rows</h4>
              <p>The final downloaded report can total the full file when the backend returns report totals.</p>
            </div>
          </ng-container>
        </article>
      </form>

      <div class="validation-panel" *ngIf="validationMessages.length">
        <h3>Review this report before saving</h3>
        <ul>
          <li *ngFor="let message of validationMessages">{{ message }}</li>
        </ul>
      </div>

      <footer class="editor-footer">
        <button mat-button type="button" (click)="cancelEditor()">Cancel</button>
        <button mat-stroked-button type="button" (click)="previewDraft()" [disabled]="isPreviewLoading || isSaving">Preview</button>
        <button
          mat-stroked-button
          type="button"
          *ngIf="currentPermissions.runManual"
          (click)="sendNowFromEditor()"
          [disabled]="isSaving || isPreviewLoading"
        >
          Send now
        </button>
        <button mat-stroked-button type="button" (click)="saveDraft()" [disabled]="isSaving">Save draft</button>
        <button mat-raised-button color="primary" type="button" (click)="activateReport()" [disabled]="isSaving || !canActivate">
          Activate
        </button>
      </footer>
    </section>

    <section *ngIf="viewMode === 'detail'" class="detail-shell">
      <section class="inline-loading" *ngIf="isDetailLoading">
        <mat-spinner [diameter]="28"></mat-spinner>
        <span>Loading report detail</span>
      </section>

      <ng-container *ngIf="!isDetailLoading && detailReport">
        <div class="detail-header">
          <div>
            <div class="detail-kicker">{{ getReportTypeLabel(detailReport.reportType) }}</div>
            <h2>{{ detailReport.name }}</h2>
            <p>{{ detailReport.description || 'No description provided.' }}</p>
          </div>

          <div class="detail-actions">
            <button mat-stroked-button type="button" *ngIf="currentPermissions.edit" (click)="editCurrentReport()">Edit</button>
            <button mat-stroked-button type="button" *ngIf="currentPermissions.pauseResume" (click)="togglePauseResume(detailReport)">
              {{ detailReport.status === 'active' ? 'Pause' : 'Resume' }}
            </button>
            <button mat-stroked-button type="button" *ngIf="currentPermissions.runManual" (click)="runNow(detailReport)">Run now</button>
            <button mat-stroked-button type="button" *ngIf="currentPermissions.rerunWithAsOf" (click)="rerunWithAsOf(detailReport, selectedRun)">
              Rerun with as-of date
            </button>
            <button mat-stroked-button color="warn" type="button" *ngIf="currentPermissions.delete" (click)="confirmDeleteReport(detailReport)">
              Archive
            </button>
            <button mat-raised-button color="primary" type="button" *ngIf="latestArtifactUrl" (click)="downloadArtifact(latestArtifactUrl)">
              Download latest artifact
            </button>
          </div>
        </div>

        <div class="detail-grid">
          <article class="panel">
            <div class="panel-header simple">
              <div>
                <h3>Configuration summary</h3>
                <p>Read-only overview of the saved report configuration.</p>
              </div>
            </div>

            <div class="summary-grid">
              <div>
                <span class="meta-label">Scope</span>
                <strong>{{ getScopeLabel(detailReport) }}</strong>
              </div>
              <div>
                <span class="meta-label">Format</span>
                <strong>{{ getFormatLabel(detailReport.outputFormat) }}</strong>
              </div>
              <div>
                <span class="meta-label">Delivery</span>
                <strong>{{ getDeliveryLabel(detailReport.deliveryChannel) }}</strong>
              </div>
              <div>
                <span class="meta-label">Schedule</span>
                <strong>{{ getScheduleSummary(detailReport) }}</strong>
              </div>
              <div>
                <span class="meta-label">Status</span>
                <strong>{{ detailReport.status }}</strong>
              </div>
              <div>
                <span class="meta-label">Next run</span>
                <strong>{{ formatReportDateTime(detailReport.nextRunAt, detailReport.timezone) }}</strong>
              </div>
            </div>

            <div class="definition-summary">
              <div>
                <span class="meta-label">Output columns</span>
                <ul>
                  <li *ngFor="let column of getDetailOutputColumns(detailReport)">{{ getDisplayColumnHeader(column) }}</li>
                  <li *ngIf="!getDetailOutputColumns(detailReport).length">No output columns configured.</li>
                </ul>
              </div>
              <div>
                <span class="meta-label">Filters</span>
                <ul>
                  <li *ngFor="let filter of detailReport.definitionJson?.filters">
                    {{ getFieldLabel(filter.field) }} {{ filter.operator }} {{ filter.value }}
                  </li>
                  <li *ngIf="!detailReport.definitionJson?.filters?.length">No filters configured.</li>
                </ul>
              </div>
              <div>
                <span class="meta-label">Sort</span>
                <ul>
                  <li *ngFor="let sort of getDetailSortRules(detailReport)">
                    {{ getFieldLabel(sort.field) }} - {{ getSortDirectionLabel(sort.direction) }}
                  </li>
                  <li *ngIf="!getDetailSortRules(detailReport).length">No sort configured.</li>
                </ul>
              </div>
            </div>
          </article>

          <article class="panel">
            <div class="panel-header simple">
              <div>
                <h3>Delivery details</h3>
                <p>Recipient or SFTP configuration summary without secret contents.</p>
              </div>
            </div>

            <div class="summary-grid" *ngIf="detailReport.deliveryChannel === 'ftp'; else emailDeliveryDetail">
              <div>
                <span class="meta-label">Host</span>
                <strong>{{ detailReport.deliveryConfig.host || '--' }}</strong>
              </div>
              <div>
                <span class="meta-label">Username</span>
                <strong>{{ detailReport.deliveryConfig.username || '--' }}</strong>
              </div>
              <div>
                <span class="meta-label">Folder</span>
                <strong>{{ detailReport.deliveryConfig.folder || '--' }}</strong>
              </div>
              <div>
                <span class="meta-label">Password secret</span>
                <strong>{{ detailReport.deliveryConfig.passwordSecretKey ? 'Configured' : 'Not configured' }}</strong>
              </div>
              <div>
                <span class="meta-label">Private key secret</span>
                <strong>{{ detailReport.deliveryConfig.privateKeySecretKey ? 'Configured' : 'Not configured' }}</strong>
              </div>
              <div>
                <span class="meta-label">PGP</span>
                <strong>{{ detailReport.deliveryConfig.pgp?.enabled ? 'Enabled' : 'Disabled' }}</strong>
              </div>
            </div>

            <ng-template #emailDeliveryDetail>
              <div class="summary-grid">
                <div>
                  <span class="meta-label">To</span>
                  <strong>{{ (detailReport.deliveryConfig.to || []).join(', ') || '--' }}</strong>
                </div>
                <div>
                  <span class="meta-label">CC</span>
                  <strong>{{ (detailReport.deliveryConfig.cc || []).join(', ') || '--' }}</strong>
                </div>
                <div>
                  <span class="meta-label">BCC</span>
                  <strong>{{ (detailReport.deliveryConfig.bcc || []).join(', ') || '--' }}</strong>
                </div>
                <div>
                  <span class="meta-label">Provider</span>
                  <strong>{{ detailReport.deliveryConfig.provider || 'Standard email' }}</strong>
                </div>
              </div>

              <div class="definition-summary">
                <div>
                  <span class="meta-label">Subject</span>
                  <p>{{ detailReport.deliveryConfig.subjectTemplate || '--' }}</p>
                </div>
                <div>
                  <span class="meta-label">Body</span>
                  <p>{{ detailReport.deliveryConfig.bodyTemplate || '--' }}</p>
                </div>
              </div>
            </ng-template>
          </article>

          <article class="panel">
            <div class="panel-header simple">
              <div>
                <h3>Latest preview snapshot</h3>
                <p>Preview output columns, sample rows, warnings, and final file name.</p>
              </div>
            </div>

            <div class="notice-card">
              <mat-slide-toggle
                [checked]="detailIncludeDemoUsers"
                [disabled]="isDetailLoading || isDetailPreviewLoading"
                (change)="changeDetailDemoPreview($event.checked)">
                Include demo users
              </mat-slide-toggle>
              <p>Applies only to this preview. Other report filters still apply.</p>
            </div>

            <div class="inline-loading detail-preview-loading" *ngIf="isDetailPreviewLoading">
              <mat-spinner [diameter]="28"></mat-spinner>
              <span>Building preview</span>
            </div>

            <div class="notice-card error" *ngIf="detailPreviewError">
              <h4>Preview failed</h4>
              <p>{{ detailPreviewError }}</p>
            </div>

            <div class="empty-inline-state" *ngIf="!detailPreview && detailReportIsSensitive && !isDetailPreviewLoading">
              <h4>Preview with SSNs masked</h4>
              <p>Full SSNs remain encrypted and are masked in the preview. Approval context is required only when revealing them.</p>
              <button mat-raised-button color="primary" type="button" (click)="previewDetailSensitiveMasked()">
                Preview with SSNs masked
              </button>
            </div>

            <div class="empty-inline-state" *ngIf="!detailPreview && !detailReportIsSensitive && !isDetailPreviewLoading">
              <h4>No preview available</h4>
              <p>Open the editor and run a preview to inspect current output columns and rows.</p>
            </div>

            <ng-container *ngIf="detailPreview">
              <div class="notice-card sensitive-preview-notice" *ngIf="detailSensitivePreviewMode">
                <div>
                  <h4>{{ detailSensitivePreviewMode === 'revealed' ? 'Full SSNs are visible' : 'SSNs are masked' }}</h4>
                  <p *ngIf="detailSensitivePreviewMode === 'masked'">Every full SSN is masked without being decrypted.</p>
                  <p *ngIf="detailSensitivePreviewMode === 'masked' && !sensitivePreviewBusinessPurposes.length">Full SSN reveal is unavailable because no approved business purposes are configured.</p>
                  <p *ngIf="detailSensitivePreviewMode === 'revealed'">Full SSNs are displayed only for the approved purpose recorded with this audited preview.</p>
                </div>
                <button
                  mat-stroked-button
                  color="warn"
                  type="button"
                  *ngIf="detailSensitivePreviewMode === 'masked'"
                  (click)="revealDetailFullSsns()"
                  [disabled]="isDetailPreviewLoading || !sensitivePreviewBusinessPurposes.length"
                >
                  Reveal full SSNs
                </button>
                <button
                  mat-stroked-button
                  type="button"
                  *ngIf="detailSensitivePreviewMode === 'revealed'"
                  (click)="maskDetailSsns()"
                >
                  Mask SSNs
                </button>
              </div>

              <div class="preview-meta">
                <div>
                  <span class="meta-label">Sample row count</span>
                  <span>{{ detailPreview.sampleRows.length }}</span>
                </div>
                <div>
                  <span class="meta-label">Total rows</span>
                  <span>{{ detailPreview.totalCount }}</span>
                </div>
                <div>
                  <span class="meta-label">Final file name</span>
                  <span>{{ detailReport.fileNamePattern || fileNamePreview }}</span>
                </div>
                <div>
                  <span class="meta-label">As-of date</span>
                  <span>{{ formatReportDateTime(detailPreview.runContext.asOfAt, detailPreview.runContext.timezone) }}</span>
                </div>
                <div>
                  <span class="meta-label">Applied data window start</span>
                  <span>{{ formatReportDateTime(detailPreview.runContext.windowStart, detailPreview.runContext.timezone) }}</span>
                </div>
                <div>
                  <span class="meta-label">Applied data window end</span>
                  <span>{{ formatReportDateTime(detailPreview.runContext.windowEnd, detailPreview.runContext.timezone) }}</span>
                </div>
              </div>

              <div class="warning-list" *ngIf="detailPreview.warnings.length">
                <h4>Warnings</h4>
                <ul>
                  <li *ngFor="let warning of detailPreview.warnings">{{ warning }}</li>
                </ul>
              </div>

              <div class="table-shell" [class.preview-scroll-shell]="shouldUseScrollablePreview(detailPreview)" *ngIf="detailPreview.sampleRows.length">
                <table class="reports-table preview-table">
                  <thead>
                    <tr>
                      <th *ngFor="let header of getColumnPreviewHeaders(detailPreview)">
                        <button type="button" class="preview-sort-button" (click)="togglePreviewSort('detail', header)">
                          <span>{{ getDisplayColumnHeader(header) }}</span>
                          <span class="preview-sort-indicator" *ngIf="getPreviewSortDirection('detail', header) === 'asc'">arrow_upward</span>
                          <span class="preview-sort-indicator" *ngIf="getPreviewSortDirection('detail', header) === 'desc'">arrow_downward</span>
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                  <tr *ngFor="let row of getSortedPreviewRows(detailPreview, 'detail')">
                    <td
                      *ngFor="let header of getColumnPreviewHeaders(detailPreview)"
                      [class.preview-cell-multiline]="isPreviewCellMultiline(header, getPreviewCellValue(detailPreview, row, header))"
                    >
                        {{ getPreviewCellText(header, getPreviewCellValue(detailPreview, row, header)) }}
                    </td>
                  </tr>
                </tbody>
                  <tfoot *ngIf="shouldShowPreviewTotals(detailPreview, 'detail')">
                    <tr class="preview-total-row">
                      <td
                        *ngFor="let header of getColumnPreviewHeaders(detailPreview)"
                        [class.preview-total-label]="isPreviewTotalLabelColumn(detailPreview, 'detail', header)"
                        [class.preview-total-value]="isPreviewTotalValueColumn(detailPreview, 'detail', header)"
                      >
                        {{ getPreviewTotalCellText(detailPreview, 'detail', header) }}
                      </td>
                    </tr>
                  </tfoot>
              </table>
              </div>

              <div class="notice-card warning" *ngIf="isPreviewTotalFallbackSampleOnly(detailPreview, 'detail')">
                <h4>Preview totals use sample rows</h4>
                <p>The final downloaded report can total the full file when the backend returns report totals.</p>
              </div>
            </ng-container>
          </article>

          <article class="panel">
            <div class="panel-header simple">
              <div>
                <h3>Run history</h3>
                <p>Review historical runs, delivery results, artifacts, and error details.</p>
              </div>
            </div>

            <div class="empty-inline-state" *ngIf="!reportRuns.length">
              <h4>No runs yet</h4>
              <p>Use Run now or activate the schedule to create the first run record.</p>
            </div>

            <div class="table-shell" *ngIf="reportRuns.length">
              <table class="reports-table">
                <thead>
                  <tr>
                    <th>Run time</th>
                    <th>Trigger type</th>
                    <th>Result</th>
                    <th>Rows</th>
                    <th>Format</th>
                    <th>As-of date</th>
                    <th>Window</th>
                    <th>Delivery</th>
                    <th class="actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let run of reportRuns; trackBy: trackById" [class.selected-row]="selectedRun?.id === run.id">
                    <td>{{ formatReportDateTime(run.startedAt || run.createdAt, detailReport.timezone) }}</td>
                    <td>{{ run.triggerType }}</td>
                    <td>{{ run.status }}</td>
                    <td>{{ run.rowsGenerated ?? '--' }}</td>
                    <td>{{ run.outputFormat || '--' }}</td>
                    <td>{{ formatReportDateTime(run.asOfAt, detailReport.timezone) }}</td>
                    <td>{{ formatReportDateRange(run.windowStart, run.windowEnd, detailReport.timezone) }}</td>
                    <td>{{ run.deliveryChannel || '--' }}</td>
                    <td class="actions-col">
                      <button mat-button type="button" (click)="selectRun(run)">Details</button>
                      <button mat-button type="button" *ngIf="run.artifactUrl" (click)="downloadArtifact(run.artifactUrl)">Download</button>
                      <button mat-button type="button" (click)="retryRun(run)">Retry</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="run-detail" *ngIf="selectedRun">
              <div class="run-detail-header">
                <h4>Run detail</h4>
                <div class="run-detail-actions">
                  <button mat-stroked-button type="button" *ngIf="selectedRun.artifactUrl" (click)="downloadArtifact(selectedRun.artifactUrl)">
                    Download artifact
                  </button>
                  <button mat-stroked-button type="button" (click)="retryRun(selectedRun)">Retry run</button>
                  <button mat-stroked-button type="button" (click)="rerunWithAsOf(detailReport, selectedRun)">Rerun with new as-of date</button>
                </div>
              </div>

              <div class="summary-grid">
                <div>
                  <span class="meta-label">Run status</span>
                  <strong>{{ selectedRun.status }}</strong>
                </div>
                <div>
                  <span class="meta-label">Started at</span>
                  <strong>{{ formatReportDateTime(selectedRun.startedAt, detailReport.timezone) }}</strong>
                </div>
                <div>
                  <span class="meta-label">Finished at</span>
                  <strong>{{ formatReportDateTime(selectedRun.finishedAt, detailReport.timezone) }}</strong>
                </div>
                <div>
                  <span class="meta-label">Rows generated</span>
                  <strong>{{ selectedRun.rowsGenerated ?? '--' }}</strong>
                </div>
                <div>
                  <span class="meta-label">Artifact</span>
                  <strong>{{ selectedRun.fileName || '--' }}</strong>
                </div>
                <div>
                  <span class="meta-label">Error details</span>
                  <strong>{{ selectedRun.errorMessage || '--' }}</strong>
                </div>
              </div>

              <div class="detail-json-grid">
                <div>
                  <span class="meta-label">Config snapshot summary</span>
                  <pre>{{ selectedRun.definitionSnapshot | json }}</pre>
                </div>
                <div>
                  <span class="meta-label">Delivery result</span>
                  <pre>{{ selectedRun.deliveryResult | json }}</pre>
                </div>
              </div>
            </div>
          </article>
        </div>
      </ng-container>
    </section>
  </ng-container>
</div>

<mat-menu #reportActionsMenu="matMenu">
  <ng-template matMenuContent let-report="report">
    <button mat-menu-item type="button" (click)="openReport(report)">
      <mat-icon>open_in_new</mat-icon>
      <span>Open</span>
    </button>
    <button mat-menu-item type="button" *ngIf="currentPermissions.pauseResume" (click)="togglePauseResume(report)">
      <mat-icon>{{ report.status === 'active' ? 'pause' : 'play_arrow' }}</mat-icon>
      <span>{{ report.status === 'active' ? 'Pause' : 'Resume' }}</span>
    </button>
    <button mat-menu-item type="button" *ngIf="currentPermissions.runManual" (click)="runNow(report)">
      <mat-icon>play_circle</mat-icon>
      <span>Run now</span>
    </button>
    <button mat-menu-item type="button" *ngIf="currentPermissions.edit" (click)="duplicateReport(report)">
      <mat-icon>content_copy</mat-icon>
      <span>Duplicate</span>
    </button>
    <button mat-menu-item type="button" *ngIf="currentPermissions.delete" (click)="confirmDeleteReport(report, $event)">
      <mat-icon>delete</mat-icon>
      <span>Archive</span>
    </button>
  </ng-template>
</mat-menu>
